/**
 * CacheAdapter - Storage para archivos pequeños usando Cache API
 *
 * Usa Cache Storage API (normalmente usado con Service Workers).
 * Ideal para: sounds, images (archivos pequeños)
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Cache
 */

import { BaseAdapter } from './BaseAdapter';

export class CacheAdapter extends BaseAdapter {
  constructor(cacheName = 'xhelos-assets') {
    super('CacheAdapter');
    this.cacheName = cacheName;
    this.cache = null;
  }

  /**
   * Inicializa acceso a Cache API
   */
  async init() {
    if (this.initialized) return;

    try {
      // Verificar soporte de Cache API
      if (!('caches' in window)) {
        throw new Error('Cache API not supported in this browser');
      }

      this.cache = await caches.open(this.cacheName);
      this.initialized = true;
    } catch (error) {
      console.error(`[CacheAdapter] Failed to init:`, error);
      this.initialized = true;
      this.cache = null;
    }
  }

  /**
   * Verifica si Cache API está disponible
   */
  isAvailable() {
    return this.cache !== null;
  }

  /**
   * Genera URL interna para el cache (Cache API usa URLs como keys)
   */
  _toUrl(key) {
    // Usamos una URL ficticia interna
    return `/_cache_/${this.cacheName}/${key}`;
  }

  /**
   * Extrae key de URL
   */
  _fromUrl(url) {
    const prefix = `/_cache_/${this.cacheName}/`;
    if (url.startsWith(prefix)) {
      return url.slice(prefix.length);
    }
    // Si es URL real, usar pathname
    try {
      const parsed = new URL(url);
      return parsed.pathname;
    } catch {
      return url;
    }
  }

  /**
   * Verifica si existe un key
   */
  async has(key) {
    this.ensureInitialized();
    if (!this.isAvailable()) return false;

    try {
      const response = await this.cache.match(this._toUrl(key));
      return response !== undefined;
    } catch (error) {
      console.warn(`[CacheAdapter] has() error:`, error);
      return false;
    }
  }

  /**
   * Obtiene un valor como Blob
   */
  async get(key) {
    this.ensureInitialized();
    if (!this.isAvailable()) return null;

    try {
      const response = await this.cache.match(this._toUrl(key));
      if (!response) return null;
      return await response.blob();
    } catch (error) {
      console.warn(`[CacheAdapter] get() error:`, error);
      return null;
    }
  }

  /**
   * Obtiene como Blob URL
   */
  async getBlobUrl(key) {
    const blob = await this.get(key);
    if (!blob) return null;
    return URL.createObjectURL(blob);
  }

  /**
   * Obtiene la Response completa (con headers)
   */
  async getResponse(key) {
    this.ensureInitialized();
    if (!this.isAvailable()) return null;

    try {
      return await this.cache.match(this._toUrl(key));
    } catch (error) {
      console.warn(`[CacheAdapter] getResponse() error:`, error);
      return null;
    }
  }

  /**
   * Guarda un Blob o Response
   * @param {string} key
   * @param {Blob|Response} value
   * @param {Object} options - { version, contentType }
   */
  async set(key, value, options = {}) {
    this.ensureInitialized();
    if (!this.isAvailable()) {
      console.warn(`[CacheAdapter] Cache not available, cannot save ${key}`);
      return;
    }

    try {
      const url = this._toUrl(key);
      let response;

      if (value instanceof Response) {
        response = value;
      } else {
        // Crear Response desde Blob
        const headers = new Headers();
        if (options.contentType) {
          headers.set('Content-Type', options.contentType);
        } else if (value.type) {
          headers.set('Content-Type', value.type);
        }
        if (options.version) {
          headers.set('X-Version', options.version);
        }
        headers.set('X-Saved-At', Date.now().toString());

        response = new Response(value, { headers });
      }

      await this.cache.put(url, response);
      console.debug(`[CacheAdapter] Saved: ${key}`);
    } catch (error) {
      console.error(`[CacheAdapter] set() error:`, error);
      throw error;
    }
  }

  /**
   * Guarda directamente desde URL (fetch + cache)
   */
  async setFromUrl(key, fetchUrl, options = {}) {
    this.ensureInitialized();
    if (!this.isAvailable()) return;

    try {
      const response = await fetch(fetchUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      // Clonar response para guardar con metadata
      const blob = await response.blob();
      await this.set(key, blob, options);
    } catch (error) {
      console.error(`[CacheAdapter] setFromUrl() error:`, error);
      throw error;
    }
  }

  /**
   * Elimina un key
   */
  async delete(key) {
    this.ensureInitialized();
    if (!this.isAvailable()) return;

    try {
      await this.cache.delete(this._toUrl(key));
    } catch (error) {
      console.warn(`[CacheAdapter] delete() error:`, error);
    }
  }

  /**
   * Limpia todo el cache
   */
  async clear() {
    this.ensureInitialized();
    if (!this.isAvailable()) return;

    try {
      await caches.delete(this.cacheName);
      this.cache = await caches.open(this.cacheName);
    } catch (error) {
      console.warn(`[CacheAdapter] clear() error:`, error);
    }
  }

  /**
   * Obtiene todas las keys
   */
  async keys() {
    this.ensureInitialized();
    if (!this.isAvailable()) return [];

    try {
      const requests = await this.cache.keys();
      return requests.map((req) => this._fromUrl(req.url));
    } catch (error) {
      console.warn(`[CacheAdapter] keys() error:`, error);
      return [];
    }
  }

  /**
   * Valida si un entry tiene la versión correcta
   */
  async validateVersion(key, currentVersion) {
    const response = await this.getResponse(key);

    if (!response) {
      return { valid: false };
    }

    const savedVersion = response.headers.get('X-Version');
    if (savedVersion && savedVersion !== currentVersion) {
      return { valid: false, outdated: true };
    }

    return { valid: true };
  }

  /**
   * Elimina entries con versión diferente
   */
  async cleanOutdatedEntries(currentVersion) {
    this.ensureInitialized();
    if (!this.isAvailable()) return 0;

    try {
      const requests = await this.cache.keys();
      let deleted = 0;

      for (const request of requests) {
        const response = await this.cache.match(request);
        const savedVersion = response?.headers.get('X-Version');

        if (savedVersion && savedVersion !== currentVersion) {
          await this.cache.delete(request);
          deleted++;
        }
      }

      return deleted;
    } catch (error) {
      console.warn(`[CacheAdapter] cleanOutdatedEntries() error:`, error);
      return 0;
    }
  }
}

export default CacheAdapter;
